import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { getUserDetails as getUserDetailsAPI } from "../services/https/users";

import { type MyDetails } from "../interfaces";
import { arrayToMapByKey, clearLocalStorage } from "../helpers";

interface UserDetails {
  user?: MyDetails["data"];
  isUserDataLoaded?: boolean;
  loginDetails?: {
    access_token: string;
    refresh_token: string;
    accept_terms?: boolean;
    accept_eula?: boolean;
  };
}

const initialState: UserDetails = {
  user: {
    id: "",
    email: "",
    username: "",
    roles: [],
    services_assigned: [],
    permissions: [],
    companies: [],
    company_config_map: {},
    accept_terms: false,
    accept_eula: false,
  },
  loginDetails: {
    access_token: "",
    refresh_token: "",
    accept_terms: false,
    accept_eula: false,
  },
};

export const getUserDetailsThunk = createAsyncThunk(
  "user/getUserDetails",
  async () => {
    const res: { data: MyDetails; status: number } = await getUserDetailsAPI();
    if (res.status !== 200) {
      clearLocalStorage();
      location.href = "/login";
    }
    let permissions: string[] = [];
    (res.data.data || []).roles.forEach((role) => {
      permissions = [...role.permissions, ...permissions];
    });
    res.data.data.permissions = permissions;
    res.data.data.company_config_map = arrayToMapByKey(
      (res.data.data as any).company_config, // For mapp the API response used any
      "company"
    );
    return res.data.data;
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setMyDetails: (state, action: PayloadAction<UserDetails>) => {
      state = action.payload;
      return state;
    },
    setTermsAndEulaAccepted: (state, action: PayloadAction<boolean>) => {
      if (state.user) {
        state.user.accept_terms = action.payload;
        state.user.accept_eula = action.payload;
      }
      if (state.loginDetails) {
        state.loginDetails.accept_terms = action.payload;
        state.loginDetails.accept_eula = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getUserDetailsThunk.fulfilled, (state, action) => {
      state.isUserDataLoaded = true;
      if (action.payload) {
        state.user = action.payload;
      } else {
        clearLocalStorage();
      }
    });
    builder.addCase(getUserDetailsThunk.pending, (state) => {
      state.isUserDataLoaded = false;
    });
    builder.addCase(getUserDetailsThunk.rejected, (state) => {
      state.isUserDataLoaded = true;
    });
  },
});

// Export the actions
export const { setMyDetails, setTermsAndEulaAccepted } = userSlice.actions;

// Export the reducer
export default userSlice.reducer;
